// Карточка кандидата: колонка детали с четырьмя вкладками. Потолок — восемь (правило 12).
//
// Вкладка живёт в адресе (?tab=applications): ссылка на отклики коллега открывает откликами,
// а не обзором. Состояние вкладок держит Radix, значение ему даёт адрес — одно состояние, а
// не два расходящихся.
//
// Действия над одним кандидатом стоят в шапке его карточки (правило 3). Их ровно два, и они
// принадлежат разным людям: смена этапа — работа рекрутёра и кадров, перевод в сотрудники —
// работа ТОЛЬКО кадров (тз 5.8.1 и матрица прав). Поэтому смена этапа — панель (этап
// меняется туда и обратно), а оформление — модалка: обратной кнопки у него нет.

import { RiUserSearchLine, RiUserSettingsLine } from '@remixicon/react';
import { useNavigate } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { CardTab as CardTabTrigger, CardTabsList } from '@/shared/ui/card-tabs';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

import { BlacklistBadge } from '../components/BlacklistResult';
import { useCan } from '../components/gates';
import { CANDIDATE_STATUS_COLORS, CANDIDATE_STATUS_TITLES } from '../dictionaries';
import { useCandidateQuery } from '../queries';
import type { CandidateTab, HiringRoute } from '../route';
import { PANEL_CANDIDATE_STATUS, candidateTab, hiringHref, openPanelHref } from '../route';

import { ApplicationsTab } from './ApplicationsTab';
import { HiringTab } from './HiringTab';
import { HistoryTab } from './HistoryTab';
import { OverviewTab } from './OverviewTab';
import { EmptyCandidateDetail } from './EmptyCandidateDetail';

const TAB_TITLES: Readonly<Record<CandidateTab, string>> = {
  overview: 'Обзор',
  applications: 'Отклики',
  history: 'История этапов',
  hiring: 'Оформление',
};

const EDIT_PERMISSION = 'hiring.candidate.edit';

/** Колонка детали представления «Кандидаты». */
export function CandidateDetail({ route }: { route: HiringRoute }) {
  if (route.rowId === undefined) {
    return <EmptyCandidateDetail route={route} />;
  }
  return <CandidateCard candidateId={route.rowId} route={route} />;
}

function CandidateCard({ candidateId, route }: { candidateId: string; route: HiringRoute }) {
  const navigate = useNavigate();
  const query = useCandidateQuery(candidateId);
  const tab = candidateTab(route.search.tab);
  const canEdit = useCan(EDIT_PERMISSION);

  return (
    <TabMenu.Root
      value={tab}
      onValueChange={(next) =>
        navigate(hiringHref(route.view, { ...route.search, tab: next }, candidateId))
      }
      className='flex h-full min-w-0 flex-1 flex-col'
    >
      <DetailPane
        title={
          <span className='flex items-center gap-2'>
            <RiUserSearchLine className='size-5 shrink-0 text-text-soft-400' />
            <span className='truncate'>{query.data?.display_name ?? 'Кандидат'}</span>
            {query.data !== undefined && (
              <>
                <Badge.Root
                  variant='lighter'
                  color={CANDIDATE_STATUS_COLORS[query.data.status]}
                  size='medium'
                >
                  {CANDIDATE_STATUS_TITLES[query.data.status]}
                </Badge.Root>
                {/* Результат сверки стоит в ШАПКЕ, а не только на вкладке «Обзор»: он меняет
                    решение по человеку целиком, и находить его переключением вкладок — это
                    ровно тот случай, когда предупреждение узнают уже после действия. */}
                <BlacklistBadge result={query.data.blacklist_result} />
              </>
            )}
          </span>
        }
        actions={
          query.data === undefined ? undefined : (
            <>
              {canEdit && query.data.status !== 'hired' && (
                <Button.Root
                  type='button'
                  variant='neutral'
                  mode='stroke'
                  size='xsmall'
                  onClick={() =>
                    navigate(openPanelHref(route, PANEL_CANDIDATE_STATUS, candidateId))
                  }
                >
                  <Button.Icon as={RiUserSettingsLine} />
                  Сменить этап
                </Button.Root>
              )}
            </>
          )
        }
        tabs={
          <CardTabsList>
            {(Object.keys(TAB_TITLES) as CandidateTab[]).map((key) => (
              <CardTabTrigger key={key} value={key}>
                {TAB_TITLES[key]}
              </CardTabTrigger>
            ))}
          </CardTabsList>
        }
      >
        <ScreenState
          data={query.data}
          error={query.error}
          subject='карточку кандидата'
          isEmpty={() => false}
          onRetry={() => void query.refetch()}
          loading={
            <div className='flex flex-col gap-3 p-5'>
              <Skeleton className='h-24 w-full' />
              <Skeleton className='h-24 w-full' />
            </div>
          }
          empty={null}
        >
          {(candidate) => (
            <>
              <TabMenu.Content value='overview'>
                <OverviewTab candidate={candidate} route={route} />
              </TabMenu.Content>
              <TabMenu.Content value='applications'>
                <ApplicationsTab candidate={candidate} />
              </TabMenu.Content>
              <TabMenu.Content value='history'>
                <HistoryTab candidate={candidate} />
              </TabMenu.Content>
              <TabMenu.Content value='hiring'>
                <HiringTab candidate={candidate} />
              </TabMenu.Content>
            </>
          )}
        </ScreenState>
      </DetailPane>
    </TabMenu.Root>
  );
}
